import { StrictMode } from "react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { ConsentBanner } from "@/features/consent/ConsentBanner";
import { RootErrorBoundary } from "@rook/shared/components/RootErrorBoundary";
import { TooltipProvider } from "@rook/shared/components/ui/tooltip";


import { App } from "./App";

import "@rook/shared/styles/globals.css";

/**
 * Entry point for `@rook/landing` — the customer portal's landing app.
 * Deploys to `clients(-tmp).portal.tryrook.io` in cuenta 3 / us-east-1.
 *
 * Provider order (outermost → innermost) — see root `src/main.tsx`
 * for the canonical rationale; landing mirrors it 1:1 so the
 * cross-package contract on session state, fetch deduping, and
 * error reporting stays predictable:
 *  - <RootErrorBoundary>   uncaught render/effect errors → /api/observability/error
 *  - <BrowserRouter>       react-router v6 history
 *  - <QueryClientProvider> TanStack Query (server-state)
 *  - <TooltipProvider>     Radix tooltip context with 300 ms delay
 *  - <StrictMode>          React 19 double-invoke catch (dev)
 *
 * `<AuthProvider>` is intentionally inside `<App>` (not here) so
 * it has router context — the auth flow needs `useNavigate()` for
 * session-expired bounces.
 *
 * `<ConsentBanner>` lives at the body root (outside any route) so
 * it's visible on every page, regardless of which route renders.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
});

const container = document.getElementById("root");
if (!container) {
  throw new Error("@rook/landing: missing #root element in index.html");
}

createRoot(container).render(
  <StrictMode>
    <RootErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider delayDuration={300}>
            <App />
            <ConsentBanner />
          </TooltipProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </RootErrorBoundary>
  </StrictMode>,
);
